import { useCallback } from 'react';

import { useFindAssignmentsByGuestAndDateLazyQuery } from '../generated/graphql';

export const useDuplicateBookingCheck = () => {
  const [findAssignments, { loading, data, error }] =
    useFindAssignmentsByGuestAndDateLazyQuery({
      errorPolicy: 'all', // Continue even if there's an error
    });

  const checkForDuplicates = useCallback(
    async (guestId: string, checkInDate: string, checkOutDate: string) => {
      try {
        const result = await findAssignments({
          variables: {
            guestId,
            checkInDate,
            checkOutDate,
          },
        });

        const overlappingBookings =
          result.data?.findAssignmentsByGuestAndDate || [];
        const hasOverlappingBookings = overlappingBookings.length > 0;

        let warningMessage: string | null = null;
        if (hasOverlappingBookings) {
          const bookingCount = overlappingBookings.length;
          const bookingText = bookingCount === 1 ? 'booking' : 'bookings';
          warningMessage = `This guest has ${bookingCount} overlapping ${bookingText} for the selected dates. Please review the details below.`;
        }

        return {
          hasOverlappingBookings,
          overlappingBookings,
          warningMessage,
        };
      } catch (err) {
        // If there's an error checking for duplicates, we'll return no duplicates
        // to avoid blocking the user flow
        console.error('Error checking for duplicate bookings:', err);
        return {
          hasOverlappingBookings: false,
          overlappingBookings: [],
          warningMessage: null,
        };
      }
    },
    [findAssignments],
  );

  return {
    checkForDuplicates,
    loading,
    duplicateData: data?.findAssignmentsByGuestAndDate
      ? {
          hasOverlappingBookings: data.findAssignmentsByGuestAndDate.length > 0,
          overlappingBookings: data.findAssignmentsByGuestAndDate,
          warningMessage:
            data.findAssignmentsByGuestAndDate.length > 0
              ? `This guest has ${data.findAssignmentsByGuestAndDate.length} overlapping ${data.findAssignmentsByGuestAndDate.length === 1 ? 'booking' : 'bookings'} for the selected dates. Please review the details below.`
              : null,
        }
      : undefined,
    error,
  };
};
